/**
 * Calendar Service
 * Handles calendar event-related API operations
 */

import { apiClient } from '@/lib/api-client';
import { API_ENDPOINTS } from '@/config/api.config';
import { PaginatedResponse } from './leadsService';

// ============================================================================
// TYPES
// ============================================================================

export type EventType =
  | 'phone_call'
  | 'video_call'
  | 'meeting_in_person'
  | 'site_visit'
  | 'unit_showing'
  | 'open_house'
  | 'quote_review'
  | 'contract_signing'
  | 'document_delivery'
  | 'follow_up'
  | 'reminder'
  | 'team_meeting'
  | 'training'
  | 'other';

export type EventStatus =
  | 'scheduled'
  | 'confirmed'
  | 'in_progress'
  | 'completed'
  | 'cancelled'
  | 'no_show'
  | 'rescheduled';

export type EventSource = 'manual' | 'google' | 'outlook' | 'system' | 'automation';

export type SyncStatus = 'not_synced' | 'pending' | 'synced' | 'conflict' | 'error';

export type Priority = 'low' | 'medium' | 'high' | 'urgent';

export type MeetingProvider = 'zoom' | 'meet' | 'teams' | 'whereby' | 'webex' | 'other';

export type MeetingResult = 'positive' | 'neutral' | 'negative' | 'reschedule' | 'no_show';

export type ResponseStatus = 'pending' | 'accepted' | 'declined' | 'tentative';

export interface CalendarEventAttendee {
  id: string;
  user_id: string;
  full_name: string;
  email: string;
  avatar?: string;
  response_status: ResponseStatus;
  is_organizer: boolean;
  is_optional: boolean;
  responded_at?: string;
  notification_sent: boolean;
}

export interface ExternalAttendee {
  email: string;
  name: string;
}

export interface Reminder {
  method: 'email' | 'push' | 'sms';
  minutes: number;
}

export interface CalendarEvent {
  id: string;

  // Basic Info
  title: string;
  description?: string;
  start_time: string;
  end_time: string;
  all_day: boolean;
  timezone: string;

  // Classification
  event_type: EventType;
  status: EventStatus;
  priority: Priority;

  // Location
  location?: string;
  meeting_link?: string;
  meeting_provider?: MeetingProvider;

  // Relations (IDs)
  user: string;
  lead?: string;
  project?: string;

  // Relations (Detail)
  user_detail?: {
    id: string;
    full_name: string;
    email: string;
    avatar?: string;
  };
  lead_detail?: {
    id: string;
    full_name: string;
    email: string;
    phone?: string;
    lead_number?: string;
  };
  project_detail?: {
    id: string;
    name: string;
    code: string;
  };

  // Attendees
  attendees_detail?: CalendarEventAttendee[];
  external_attendees?: ExternalAttendee[];

  // Sync
  source: EventSource;
  external_id?: string;
  sync_status: SyncStatus;
  last_synced_at?: string;

  // Reminders & UI
  reminders?: Reminder[];
  color: string;

  // Result
  meeting_result?: MeetingResult;
  notes?: string;

  // Computed
  duration_minutes?: number;
  is_past?: boolean;
  is_today?: boolean;

  // Timestamps
  created_at: string;
  updated_at: string;
}

export interface CalendarEventListItem {
  id: string;
  title: string;
  start_time: string;
  end_time: string;
  all_day: boolean;
  event_type: EventType;
  status: EventStatus;
  priority: Priority;
  color: string;
  location?: string;
  meeting_link?: string;
  user: string;
  user_name: string;
  lead?: string;
  lead_name?: string;
  project?: string;
  project_name?: string;
  attendees_count: number;
  duration_minutes: number;
  source: EventSource;
  sync_status: SyncStatus;
}

export interface CalendarEventCreateData {
  title: string;
  description?: string;
  start_time: string;
  end_time: string;
  all_day?: boolean;
  timezone?: string;
  event_type?: EventType;
  status?: EventStatus;
  priority?: Priority;
  location?: string;
  meeting_link?: string;
  meeting_provider?: MeetingProvider;
  lead?: string;
  client?: string;
  project?: string;
  /** Company UUID - used by platform users to assign events to tenants */
  company?: string;
  /** User UUID - used by platform users to assign events to specific advisors */
  user?: string;
  attendee_ids?: string[];
  external_attendees?: ExternalAttendee[];
  reminders?: Reminder[];
  color?: string;
  notes?: string;
}

export interface CalendarEventUpdateData extends Partial<CalendarEventCreateData> {}

export interface RSVPData {
  response: 'accepted' | 'declined' | 'tentative';
}

export interface ChangeStatusData {
  status: EventStatus;
  notes?: string;
}

export interface CompleteEventData {
  result: MeetingResult;
  notes?: string;
}

export interface CalendarFilters {
  start_date?: string;
  end_date?: string;
  date?: string;
  month?: string;
  user_id?: string;
  lead_id?: string;
  project_id?: string;
  event_type?: EventType | EventType[];
  status?: EventStatus | EventStatus[];
  source?: EventSource | EventSource[];
  priority?: Priority | Priority[];
  is_past?: boolean;
  has_lead?: boolean;
  has_meeting_link?: boolean;
  search?: string;
  ordering?: string;
  page?: number;
  page_size?: number;
}

export interface BusyTime {
  user_id: string;
  start_time: string;
  end_time: string;
}

export interface AvailabilityResponse {
  start_date: string;
  end_date: string;
  duration_minutes: number;
  busy_times: BusyTime[];
}

export interface CalendarViewResponse {
  month: string;
  events_by_date: Record<string, CalendarEventListItem[]>;
  total_events: number;
}

/**
 * EventInstance que aparece en el feed del calendario.
 * Es una sub-categoría de fuente (feria, evento) con fechas concretas.
 */
export interface CalendarFeedEventInstance {
  id: string;
  kind: 'event_instance';
  name: string;
  template_name: string;
  template_organizer: string;
  start_date: string | null;
  end_date: string | null;
  year: number;
  location: string;
  leads_count: number;
  is_active: boolean;
  project_id: string;
}

/**
 * Respuesta del endpoint /api/calendar/events/feed/ — agrega CalendarEvent
 * (citas, reuniones, tareas) + EventInstance (ferias del proyecto) en una
 * sola llamada para que el componente Calendar renderice todo junto.
 * Festivos siguen viviendo en useHolidayMap (cache localStorage).
 */
export interface CalendarFeedResponse {
  month: string;
  events_by_date: Record<string, CalendarEventListItem[]>;
  event_instances: CalendarFeedEventInstance[];
  total_events: number;
  total_event_instances: number;
}

export interface TodayEventsResponse {
  date: string;
  count: number;
  events: CalendarEventListItem[];
}

export interface UpcomingEventsResponse {
  count: number;
  events: CalendarEventListItem[];
}

export interface LeadHistoryResponse {
  lead_id: string;
  count: number;
  events: CalendarEventListItem[];
}

// ============================================================================
// SERVICE
// ============================================================================

/**
 * Calendar Service
 */
export const calendarService = {
  /**
   * Get all calendar events with filters
   */
  async getEvents(params?: CalendarFilters): Promise<PaginatedResponse<CalendarEventListItem>> {
    const queryParams = new URLSearchParams();

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (Array.isArray(value)) {
            value.forEach(v => queryParams.append(key, v.toString()));
          } else {
            queryParams.append(key, value.toString());
          }
        }
      });
    }

    const endpoint = params
      ? `${API_ENDPOINTS.CALENDAR.BASE}?${queryParams.toString()}`
      : API_ENDPOINTS.CALENDAR.BASE;

    return apiClient.get<PaginatedResponse<CalendarEventListItem>>(endpoint);
  },

  /**
   * Get event by ID
   */
  async getEventById(id: string): Promise<CalendarEvent> {
    return apiClient.get<CalendarEvent>(API_ENDPOINTS.CALENDAR.DETAIL(id));
  },

  /**
   * Create new calendar event
   */
  async createEvent(data: CalendarEventCreateData): Promise<CalendarEvent> {
    return apiClient.post<CalendarEvent>(API_ENDPOINTS.CALENDAR.BASE, data);
  },

  /**
   * Update calendar event
   */
  async updateEvent(id: string, data: CalendarEventUpdateData): Promise<CalendarEvent> {
    return apiClient.patch<CalendarEvent>(API_ENDPOINTS.CALENDAR.DETAIL(id), data);
  },

  /**
   * Delete calendar event (soft delete)
   */
  async deleteEvent(id: string): Promise<void> {
    return apiClient.delete<void>(API_ENDPOINTS.CALENDAR.DETAIL(id));
  },

  /**
   * RSVP to an event invitation
   */
  async rsvp(eventId: string, data: RSVPData): Promise<{
    event_id: string;
    response_status: string;
    responded_at: string;
  }> {
    return apiClient.post(API_ENDPOINTS.CALENDAR.RSVP(eventId), data);
  },

  /**
   * Change event status
   */
  async changeStatus(eventId: string, data: ChangeStatusData): Promise<CalendarEvent> {
    return apiClient.post<CalendarEvent>(API_ENDPOINTS.CALENDAR.CHANGE_STATUS(eventId), data);
  },

  /**
   * Complete event with result
   */
  async completeEvent(eventId: string, data: CompleteEventData): Promise<CalendarEvent> {
    return apiClient.post<CalendarEvent>(API_ENDPOINTS.CALENDAR.COMPLETE(eventId), data);
  },

  /**
   * Get upcoming events (next N days)
   */
  async getUpcoming(days: number = 7): Promise<UpcomingEventsResponse> {
    const endpoint = `${API_ENDPOINTS.CALENDAR.UPCOMING}?days=${days}`;
    return apiClient.get<UpcomingEventsResponse>(endpoint);
  },

  /**
   * Get today's events
   */
  async getToday(): Promise<TodayEventsResponse> {
    return apiClient.get<TodayEventsResponse>(API_ENDPOINTS.CALENDAR.TODAY);
  },

  /**
   * Get availability (busy times) for users
   */
  async getAvailability(params: {
    user_ids: string[];
    start_date: string;
    end_date: string;
    duration_minutes?: number;
  }): Promise<AvailabilityResponse> {
    const queryParams = new URLSearchParams();
    params.user_ids.forEach(id => queryParams.append('user_ids', id));
    queryParams.append('start_date', params.start_date);
    queryParams.append('end_date', params.end_date);
    if (params.duration_minutes) {
      queryParams.append('duration_minutes', params.duration_minutes.toString());
    }

    const endpoint = `${API_ENDPOINTS.CALENDAR.AVAILABILITY}?${queryParams.toString()}`;
    return apiClient.get<AvailabilityResponse>(endpoint);
  },

  /**
   * Get calendar view (events grouped by date)
   *
   * For platform users (OWLY):
   * - Pass company and project to filter by tenant context
   * - user_id is optional (if not provided, shows all events for the company/project)
   *
   * For tenant users:
   * - user_id defaults to current user on backend
   */
  async getCalendarView(params: {
    month: string;  // YYYY-MM format
    user_id?: string;
    company?: string;
    project?: string;
  }): Promise<CalendarViewResponse> {
    const queryParams = new URLSearchParams();
    queryParams.append('month', params.month);
    if (params.user_id) {
      queryParams.append('user_id', params.user_id);
    }
    if (params.company) {
      queryParams.append('company', params.company);
    }
    if (params.project) {
      queryParams.append('project', params.project);
    }

    const endpoint = `${API_ENDPOINTS.CALENDAR.CALENDAR_VIEW}?${queryParams.toString()}`;
    return apiClient.get<CalendarViewResponse>(endpoint);
  },

  /**
   * Feed unificado: CalendarEvent + EventInstance (ferias del proyecto)
   * en una sola llamada. Reemplaza calendar-view para vistas que muestran
   * eventos de proyecto (incluye sub-categoría de eventos como ferias).
   */
  async getCalendarFeed(params: {
    month: string;
    project: string;
    user_id?: string;
    company?: string;
  }): Promise<CalendarFeedResponse> {
    const queryParams = new URLSearchParams();
    queryParams.append('month', params.month);
    queryParams.append('project', params.project);
    if (params.user_id) queryParams.append('user_id', params.user_id);
    if (params.company) queryParams.append('company', params.company);

    const endpoint = `${API_ENDPOINTS.CALENDAR.FEED}?${queryParams.toString()}`;
    return apiClient.get<CalendarFeedResponse>(endpoint);
  },

  /**
   * Get event history for a lead
   */
  async getLeadHistory(leadId: string): Promise<LeadHistoryResponse> {
    const endpoint = `${API_ENDPOINTS.CALENDAR.LEAD_HISTORY}?lead_id=${leadId}`;
    return apiClient.get<LeadHistoryResponse>(endpoint);
  },
};

export default calendarService;
